// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { CaretUp } from "@phosphor-icons/react";
import { Badge, Text } from "@radix-ui/themes";
import { useLayoutEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AskV2ReviewCommentJudgmentActions } from "./AskV2ReviewCommentJudgmentActions";
import { AskV2ReviewTagFilter } from "./AskV2ReviewTagFilter";
import { CommentTagBadges } from "./CommentTagBadges";
import {
  defaultActiveReviewCommentTags,
  type CommentWithOptionalTags,
  type ReviewCommentTagId,
} from "./reviewCommentTags";

import {
  AskPunttReviewCommentBadge,
  AskPunttReviewCommentBody,
  AskPunttReviewCommentSource,
  type AssetFile,
  formatReviewCommentTime,
  reviewCommentAuthorLabel,
  reviewCommentCollapsedSections,
  reviewCommentTitle,
} from "../../components/ask-puntt-review";
import { claimCitationsFor } from "../../components/ask-puntt-review/claimCitations";
import { type LinkableClaim } from "../../plugins/rehype-claim-citation";

type AskV2ReviewCommentsListProps = {
  comments: readonly CommentWithOptionalTags[];
  commentNumbers?: Readonly<Record<string, number>>;
  contextFiles?: readonly AssetFile[];
  reviewFiles?: readonly AssetFile[];
  selectedCommentId?: string | null;
  authorNameByUserId?: Readonly<Record<string, string>>;
  currentUserId?: string;
  currentUserName?: string;
  activeTags?: ReadonlySet<ReviewCommentTagId>;
  showTagFilter?: boolean;
  onToggleTag?: (tag: ReviewCommentTagId, checked: boolean) => void;
  includeRejectedComments?: boolean;
  rejectedCommentCount?: number;
  onToggleIncludeRejected?: () => void;
  judgmentPending?: boolean;
  onJudge?: (commentId: string, liked: boolean | null) => void;
  onSelect: (commentId: string) => void;
  onClear: () => void;
};

function CommentSeverityBadge({
  severity,
}: {
  severity: CommentWithOptionalTags["severity"];
}) {
  const { t } = useTranslation("ui");
  if (!severity) return null;

  const label =
    severity === "high"
      ? t("highConfidence")
      : severity === "medium"
        ? t("mediumConfidence")
        : t("lowConfidence");

  return (
    <Badge
      size="1"
      variant="soft"
      color="gray"
      className="inline-flex items-center gap-1"
      data-testid="ask-v2-review-comment-confidence-badge"
    >
      <CaretUp size={10} weight="bold" aria-hidden="true" />
      {label}
    </Badge>
  );
}

function CollapsedCommentPreview({
  comment,
}: {
  comment: CommentWithOptionalTags;
}) {
  const sections = reviewCommentCollapsedSections(comment.description);
  if (sections.length === 0) {
    const preview = comment.description.trim().split("\n").slice(1).join(" ");
    if (!preview) return null;
    return (
      <Text as="p" size="3" className="mt-2 text-puntt-neutral-gray-10">
        {preview}
      </Text>
    );
  }

  return (
    <div
      className="mt-2 space-y-2"
      data-testid={`ask-v2-review-comment-preview-${comment._id}`}
    >
      {sections.map((section) => (
        <Text
          as="p"
          key={section.label}
          size="3"
          className="text-puntt-neutral-gray-11"
          data-testid={`ask-v2-review-comment-section-${comment._id}-${section.label.toLowerCase()}`}
        >
          <span className="font-semibold text-puntt-neutral-gray-12">
            {section.label}:
          </span>{" "}
          {section.body}
        </Text>
      ))}
    </div>
  );
}

export function AskV2ReviewCommentsList({
  comments,
  commentNumbers: commentNumbersProp,
  contextFiles = [],
  reviewFiles = [],
  selectedCommentId = null,
  authorNameByUserId,
  currentUserId,
  currentUserName,
  activeTags = defaultActiveReviewCommentTags(),
  showTagFilter = false,
  onToggleTag = () => undefined,
  includeRejectedComments = false,
  rejectedCommentCount = 0,
  onToggleIncludeRejected,
  judgmentPending = false,
  onJudge,
  onSelect,
  onClear,
}: AskV2ReviewCommentsListProps) {
  const { t } = useTranslation("ui");
  const commentNumbers = useMemo(() => {
    if (commentNumbersProp) return commentNumbersProp;
    const numbers: Record<string, number> = {};
    let index = 0;
    for (const comment of comments) {
      if (comment.isDeleted) continue;
      index += 1;
      numbers[comment._id] = index;
    }
    return numbers;
  }, [commentNumbersProp, comments]);

  // Derived once per comment list, not inside the render map. Called inline it
  // handed `AskPunttReviewCommentBody` a fresh array identity on every render
  // of this list -- and that array is what `Markdown` keys its rehype-plugin
  // memo on, so every unrelated re-render (a tag filter, a selection) rebuilt
  // the unified processor for every comment on screen. `ChatMessage` already
  // memoises the same derivation; this is the list doing the same.
  const claimCitationsByComment = useMemo(() => {
    const byId = new Map<string, LinkableClaim[]>();
    for (const comment of comments) {
      byId.set(comment._id, claimCitationsFor(comment));
    }
    return byId;
  }, [comments]);

  const authorOptions = {
    aiLabel: t("aiReviewer"),
    currentUserId,
    currentUserName,
    authorNameByUserId,
    unknownAuthorLabel: t("member"),
  };

  useLayoutEffect(() => {
    if (!selectedCommentId) return;
    document
      .querySelector(
        `[data-testid="ask-v2-review-comment-card-${selectedCommentId}"]`,
      )
      ?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [selectedCommentId]);

  return (
    <section
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="ask-v2-review-comments"
      aria-label={t("askV2.review.findingsLabel")}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
        <h2
          className="text-[13px] font-bold text-puntt-neutral-gray-12"
          data-testid="ask-v2-review-findings-title"
        >
          {t("askV2.review.findingsTitle")}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          {rejectedCommentCount > 0 && onToggleIncludeRejected ? (
            <button
              type="button"
              className="text-[12px] font-medium text-puntt-neutral-gray-11 underline-offset-2 hover:underline"
              data-testid="ask-v2-review-show-rejected-toggle"
              aria-pressed={includeRejectedComments}
              onClick={onToggleIncludeRejected}
            >
              {t("askV2.review.showRejectedToggle", {
                count: rejectedCommentCount,
              })}
            </button>
          ) : null}
          {showTagFilter ? (
            <AskV2ReviewTagFilter
              activeTags={activeTags}
              onToggle={onToggleTag}
            />
          ) : null}
        </div>
      </div>
      <ul
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
        data-testid="ask-v2-review-comment-list"
        aria-label={t("askV2.review.commentsLabel")}
      >
        {comments.length === 0 ? (
          <li
            className="px-2 py-6 text-center text-[13px] text-puntt-neutral-gray-10"
            data-testid={
              rejectedCommentCount > 0 && !includeRejectedComments
                ? "ask-v2-review-all-rejected-empty"
                : "ask-v2-review-tag-filter-empty"
            }
          >
            {rejectedCommentCount > 0 && !includeRejectedComments
              ? t("askV2.review.allRejectedEmpty")
              : t("askV2.review.tagFilterEmpty")}
          </li>
        ) : null}
        {comments.map((comment) => {
          const id = comment._id;
          const expanded = selectedCommentId === id;
          const title = reviewCommentTitle(comment);
          const commentNumber = commentNumbers[id] ?? 0;
          const collapsedSurface =
            "bg-puntt-neutral-gray-3 hover:bg-base-white transition-colors";
          const expandedSurface = "bg-base-white";

          return (
            <li key={id} className="mb-2">
              <div
                className={`overflow-hidden rounded-lg border border-puntt-neutral-gray-5 ${
                  expanded ? expandedSurface : collapsedSurface
                }`}
                data-testid={`ask-v2-review-comment-card-${id}`}
              >
                <button
                  type="button"
                  className={`w-full p-3 text-left ${
                    expanded ? expandedSurface : collapsedSurface
                  }`}
                  data-testid={`ask-v2-review-comment-${id}`}
                  aria-expanded={expanded}
                  onClick={() => (expanded ? onClear() : onSelect(id))}
                >
                  {expanded ? (
                    <>
                      <div className="flex items-start gap-3">
                        <AskPunttReviewCommentBadge
                          number={commentNumber}
                          data-testid={`ask-v2-review-comment-index-${id}`}
                        />
                        <div className="min-w-0 flex-1 text-left">
                          <Text
                            as="p"
                            size="3"
                            weight="medium"
                            className="leading-snug text-puntt-neutral-gray-12"
                            data-testid={`ask-v2-review-comment-title-${id}`}
                          >
                            {title}
                          </Text>
                          <div
                            className="mt-1 flex min-w-0 items-center gap-2 text-[12px] text-puntt-neutral-gray-9"
                            data-testid={`ask-v2-review-comment-author-${id}`}
                          >
                            <span className="font-medium text-puntt-neutral-gray-11">
                              {reviewCommentAuthorLabel(comment, authorOptions)}
                            </span>
                            <span>
                              {formatReviewCommentTime(comment.createdAt)}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {comment.isRequired ? (
                              <Badge
                                size="1"
                                variant="solid"
                                color="red"
                                data-testid="ask-v2-review-comment-urgent-badge"
                              >
                                {t("askV2.review.urgentBadge")}
                              </Badge>
                            ) : null}
                            <CommentSeverityBadge severity={comment.severity} />
                            <CommentTagBadges
                              comment={comment}
                              commentId={id}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="line-clamp-5 text-left">
                      <div className="flex items-start gap-3">
                        <AskPunttReviewCommentBadge
                          number={commentNumber}
                          data-testid={`ask-v2-review-comment-index-${id}`}
                        />
                        <Text
                          as="p"
                          size="3"
                          weight="medium"
                          className="min-w-0 flex-1 leading-snug text-puntt-neutral-gray-12"
                          data-testid={`ask-v2-review-comment-title-${id}`}
                        >
                          {title}
                        </Text>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 pl-10">
                        <CommentTagBadges comment={comment} commentId={id} />
                      </div>
                      <CollapsedCommentPreview comment={comment} />
                    </div>
                  )}
                </button>
                {expanded ? (
                  <div
                    className="bg-base-white px-3 pb-3 pt-0"
                    data-testid={`ask-v2-review-comment-expanded-${id}`}
                  >
                    <AskPunttReviewCommentBody
                      commentId={id}
                      description={comment.description}
                      claimCitations={claimCitationsByComment.get(id)}
                      afterExpand={
                        <AskPunttReviewCommentSource
                          comment={comment}
                          contextFiles={contextFiles}
                          reviewFiles={reviewFiles}
                        />
                      }
                    />
                    {comment.isAI &&
                    !comment.isPending &&
                    !comment.isDeleted &&
                    onJudge ? (
                      <AskV2ReviewCommentJudgmentActions
                        commentId={id}
                        liked={comment.liked}
                        includeRejected={includeRejectedComments}
                        disabled={judgmentPending}
                        onJudge={(liked) => onJudge(id, liked)}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
